import React, { useContext, useState, useEffect } from 'react';
import { ThemeContext } from '../Context/Context';
import { Landingdata } from '../../Data/Landingdata';

const Landing = () => {
  const { darkMode } = useContext(ThemeContext);
  const [currentRole, setCurrentRole] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFocus, setSelectedFocus] = useState({
    label: 'ML',
    title: 'Applied Intelligence',
    text: 'Predictive systems and practical AI workflows',
  });

  const roles = Landingdata.about.split(/[\s,]+/).filter(role => role.length > 0);
  const focusItems = [
    { label: 'ML', title: 'Applied Intelligence', text: 'Predictive systems and practical AI workflows', className: 'chip-ml' },
    { label: 'Cloud', title: 'Cloud Systems', text: 'Reliable deployment, scalable infra, and modern platforms', className: 'chip-cloud' },
    { label: 'Data', title: 'Data Thinking', text: 'Analysis, visualization, and insight-driven products', className: 'chip-data' },
    { label: 'Frontend', title: 'Frontend Craft', text: 'Fast, responsive, human-centered interfaces', className: 'chip-front' },
    { label: 'Research', title: 'Research Mindset', text: 'Academic rigor meeting useful software systems', className: 'chip-research' },
  ];

  useEffect(() => {
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;

    const timer = setTimeout(() => {
      const currentFullRole = roles[roleIndex];

      if (!isDeleting && charIndex < currentFullRole.length) {
        setCurrentRole(prev => prev + currentFullRole[charIndex]);
        setCharIndex(prev => prev + 1);
      } else if (isDeleting && charIndex > 0) {
        setCurrentRole(prev => prev.slice(0, -1));
        setCharIndex(prev => prev - 1);
      } else if (!isDeleting && charIndex === currentFullRole.length) {
        setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && charIndex === 0) {
        setIsDeleting(false);
        setRoleIndex((prev) => (prev + 1) % roles.length);
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, roleIndex, roles]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .modern-landing {
          min-height: 100vh;
          position: relative;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
          transition: background 0.5s ease;
        }

        /* Light Mode - Clean and Modern */
        .modern-landing.light {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        /* Dark Mode - Deep and Rich */
        .modern-landing.dark {
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1e293b 100%);
        }

        /* Animated Background Shapes */
        .bg-shapes {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
          z-index: 0;
        }

        .shape {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          animation: float 20s infinite ease-in-out;
        }

        /* Light Mode Shapes - Subtle and Modern */
        .light .shape-1 {
          top: 10%;
          left: 10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, transparent 70%);
          animation-delay: 0s;
        }

        .light .shape-2 {
          top: 50%;
          right: 10%;
          width: 450px;
          height: 450px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
          animation-delay: 5s;
        }

        .light .shape-3 {
          bottom: 10%;
          left: 30%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
          animation-delay: 10s;
        }

        /* Dark Mode Shapes */
        .dark .shape-1 {
          top: 10%;
          left: 10%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.5) 0%, transparent 70%);
          opacity: 0.15;
          animation-delay: 0s;
        }

        .dark .shape-2 {
          top: 50%;
          right: 10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(245, 158, 11, 0.5) 0%, transparent 70%);
          opacity: 0.15;
          animation-delay: 5s;
        }

        .dark .shape-3 {
          bottom: 10%;
          left: 30%;
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.5) 0%, transparent 70%);
          opacity: 0.15;
          animation-delay: 10s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(40px, -40px) scale(1.15); }
          66% { transform: translate(-30px, 30px) scale(0.95); }
        }

        /* Particles - Only for Dark Mode */
        .dark .particles {
          position: absolute;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 0;
        }

        .light .particles { display: none; }

        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 50%;
          animation: particleFloat 15s infinite ease-in-out;
        }

        @keyframes particleFloat {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
        }

        .particle:nth-child(1) { left: 10%; animation-delay: 0s; }
        .particle:nth-child(2) { left: 20%; animation-delay: 2s; }
        .particle:nth-child(3) { left: 30%; animation-delay: 4s; }
        .particle:nth-child(4) { left: 40%; animation-delay: 1s; }
        .particle:nth-child(5) { left: 50%; animation-delay: 3s; }
        .particle:nth-child(6) { left: 60%; animation-delay: 5s; }
        .particle:nth-child(7) { left: 70%; animation-delay: 2.5s; }
        .particle:nth-child(8) { left: 80%; animation-delay: 4.5s; }
        .particle:nth-child(9) { left: 90%; animation-delay: 1.5s; }

        /* Main Content */
        .landing-content {
          position: relative;
          z-index: 1;
          min-height: 100vh;
          display: grid;
          grid-template-columns: minmax(600px, 1.08fr) minmax(340px, 0.72fr);
          gap: clamp(2rem, 4vw, 3.25rem);
          align-items: center;
          padding: 180px 2rem 2rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .content-wrapper {
          max-width: 860px;
          width: 100%;
        }

        /* Greeting Badge */
        .greeting {
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          margin-bottom: 1.5rem;
          display: inline-block;
          padding: 0.6rem 1.5rem;
          border-radius: 50px;
          animation: fadeInDown 0.8s ease;
        }

        .light .greeting {
          color: #6366f1;
          background: rgba(99, 102, 241, 0.08);
          border: 1px solid rgba(99, 102, 241, 0.2);
        }

        .dark .greeting {
          color: rgba(255, 255, 255, 0.95);
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Name */
        .name {
          font-size: 3.65rem;
          font-weight: 900;
          margin: 1.5rem 0;
          line-height: 1.02;
          animation: fadeInUp 0.8s ease 0.2s both;
          white-space: nowrap;
        }

        .light .name {
          color: #0f172a;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .dark .name {
          color: white;
          text-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        }

        .name span {
          display: inline-block;
        }

        .light .name span {
          background: linear-gradient(120deg, #6366f1 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .dark .name span {
          background: linear-gradient(120deg, #ffffff 0%, #fbbf24 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Title with Typewriter */
        .title-container {
          min-height: 80px;
          margin-bottom: 2rem;
          animation: fadeInUp 0.8s ease 0.4s both;
        }

        .title {
          font-size: 2.25rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .light .title {
          color: #475569;
        }

        .dark .title {
          color: rgba(255, 255, 255, 0.95);
        }

        .typewriter-text {
          min-width: 20px;
          display: inline-block;
        }

        .light .typewriter-text {
          background: linear-gradient(120deg, #10b981 0%, #059669 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .dark .typewriter-text {
          background: linear-gradient(120deg, #fbbf24 0%, #f59e0b 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .cursor {
          display: inline-block;
          width: 3px;
          height: 1.8rem;
          animation: blink 1s infinite;
          margin-left: 4px;
        }

        .light .cursor { background: #10b981; }
        .dark .cursor { background: #fbbf24; }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        /* Description */
        .description {
          font-size: 1.2rem;
          line-height: 1.9;
          margin-bottom: 3rem;
          max-width: 650px;
          animation: fadeInUp 0.8s ease 0.6s both;
          font-weight: 400;
          text-align: justify;
          text-align-last: left;
        }

        .light .description {
          color: #475569;
        }

        .dark .description {
          color: rgba(255, 255, 255, 0.88);
        }

        /* CTA Buttons */
        .cta-buttons {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
          animation: fadeInUp 0.8s ease 0.8s both;
        }

        .btn {
          padding: 1.1rem 2.8rem;
          font-size: 0.95rem;
          font-weight: 700;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.7rem;
          position: relative;
          overflow: hidden;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .btn::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }

        .btn:hover::before {
          width: 400px;
          height: 400px;
        }

        .btn span {
          position: relative;
          z-index: 1;
        }

        /* Light Mode Buttons */
        .light .btn-primary {
          background: linear-gradient(135deg, #6366f1 0%, #3b82f6 100%);
          color: white;
          box-shadow: 0 8px 24px rgba(99, 102, 241, 0.25);
        }

        .light .btn-primary:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(99, 102, 241, 0.35);
        }

        .light .btn-secondary {
          background: rgba(99, 102, 241, 0.08);
          color: #6366f1;
          border: 2px solid rgba(99, 102, 241, 0.3);
        }

        .light .btn-secondary:hover {
          background: rgba(99, 102, 241, 0.15);
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(99, 102, 241, 0.2);
          border-color: rgba(99, 102, 241, 0.5);
        }

        /* Dark Mode Buttons */
        .dark .btn-primary {
          background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
          color: white;
          box-shadow: 0 12px 35px rgba(245, 158, 11, 0.4);
        }

        .dark .btn-primary:hover {
          transform: translateY(-4px);
          box-shadow: 0 18px 45px rgba(245, 158, 11, 0.5);
        }

        .dark .btn-secondary {
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(10px);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.4);
        }

        .dark .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-4px);
          box-shadow: 0 12px 35px rgba(255, 255, 255, 0.25);
          border-color: rgba(255, 255, 255, 0.6);
        }

        .hero-orbit {
          position: relative;
          width: min(100%, 410px);
          aspect-ratio: 1;
          justify-self: end;
          animation: fadeInUp 0.8s ease 0.9s both;
        }

        .orbit-ring {
          position: absolute;
          inset: 10%;
          border: 1px solid rgba(148, 163, 184, 0.22);
          border-radius: 50%;
          animation: orbitSpin 24s linear infinite;
        }

        .orbit-ring:nth-child(2) {
          inset: 22%;
          animation-duration: 18s;
          animation-direction: reverse;
        }

        .orbit-core {
          position: absolute;
          inset: 32%;
          border-radius: 28px;
          display: grid;
          place-items: center;
          text-align: center;
          padding: 1.2rem;
          font-weight: 900;
          line-height: 1.05;
          box-shadow: 0 22px 70px rgba(15, 23, 42, 0.14);
          transition: transform 0.3s ease, border-color 0.3s ease;
        }

        .orbit-core:hover {
          transform: translateY(-4px);
        }

        .light .orbit-core {
          background: rgba(255, 255, 255, 0.86);
          border: 1px solid rgba(15, 23, 42, 0.1);
          color: #0f172a;
        }

        .dark .orbit-core {
          background: rgba(15, 23, 42, 0.78);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: #ffffff;
        }

        .orbit-core span {
          display: block;
          font-size: 0.8rem;
          font-weight: 700;
          color: #c8db42;
          margin-bottom: 0.35rem;
        }

        .orbit-core p {
          margin: 0.6rem 0 0;
          font-size: 0.78rem;
          line-height: 1.45;
          font-weight: 600;
          color: #64748b;
        }

        .dark .orbit-core p {
          color: rgba(255, 255, 255, 0.72);
        }

        .tech-chip {
          position: absolute;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 96px;
          padding: 0.75rem 1rem;
          border-radius: 999px;
          font-size: 0.92rem;
          font-weight: 800;
          letter-spacing: 0;
          box-shadow: 0 16px 42px rgba(15, 23, 42, 0.12);
          animation: chipFloat 5.5s ease-in-out infinite;
          cursor: pointer;
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }

        .tech-chip:hover,
        .tech-chip.active {
          transform: translateY(-8px) scale(1.03);
          border-color: var(--chip-color);
          box-shadow: 0 18px 50px rgba(15, 23, 42, 0.18);
        }

        .light .tech-chip {
          color: #111111;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(17, 17, 17, 0.1);
        }

        .dark .tech-chip {
          color: #f8fafc;
          background: rgba(16, 17, 14, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }

        .tech-chip::before {
          content: '';
          width: 9px;
          height: 9px;
          border-radius: 999px;
          margin-right: 0.55rem;
          background: var(--chip-color);
          box-shadow: 0 0 18px var(--chip-color);
        }

        .chip-ml { --chip-color: #c8db42; top: 8%; left: 30%; animation-delay: 0s; }
        .chip-cloud { --chip-color: #38bdf8; top: 34%; right: 0; animation-delay: 0.7s; }
        .chip-data { --chip-color: #f97316; bottom: 14%; right: 12%; animation-delay: 1.3s; }
        .chip-front { --chip-color: #a78bfa; bottom: 20%; left: 0; animation-delay: 2s; }
        .chip-research { --chip-color: #2dd4bf; top: 38%; left: 3%; animation-delay: 2.7s; }

        @keyframes orbitSpin {
          to { transform: rotate(360deg); }
        }

        @keyframes chipFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }

        /* Responsive Design */
        @media screen and (max-width: 1200px) {
          .landing-content { padding-top: 160px; }
          .name { font-size: 3.2rem; }
          .title { font-size: 2rem; }
        }

        @media screen and (max-width: 980px) {
          .landing-content {
            grid-template-columns: 1fr;
            gap: 1.75rem;
          }

          .hero-orbit {
            justify-self: center;
            width: min(100%, 360px);
          }

          .name {
            white-space: normal;
          }
        }

        @media screen and (max-width: 768px) {
          .landing-content { padding: 140px 1.5rem 2rem 1.5rem; }
          .greeting { font-size: 0.8rem; padding: 0.5rem 1.3rem; letter-spacing: 2px; }
          .name { font-size: 2.35rem; margin: 1rem 0; }
          .title-container { min-height: 70px; }
          .title { font-size: 1.6rem; }
          .cursor { height: 1.4rem; }
          .description { font-size: 1.05rem; line-height: 1.7; text-align: left; }
          .cta-buttons { flex-direction: column; gap: 1rem; }
          .btn { width: 100%; justify-content: center; padding: 1rem 2rem; }
          .tech-chip { min-width: 84px; font-size: 0.78rem; padding: 0.62rem 0.8rem; }
        }

        @media screen and (max-width: 480px) {
          .landing-content { padding: 120px 1rem 2rem 1rem; }
          .greeting { font-size: 0.7rem; padding: 0.45rem 1.1rem; letter-spacing: 2px; }
          .name { font-size: 2rem; }
          .title { font-size: 1.4rem; }
          .cursor { height: 1.2rem; }
          .description { font-size: 0.95rem; }
          .btn { padding: 0.9rem 1.8rem; font-size: 0.85rem; }
          .hero-orbit { width: min(100%, 310px); }
          .orbit-core { border-radius: 20px; font-size: 0.88rem; }
        }
      `}</style>

      <div className={`modern-landing ${darkMode ? 'dark' : 'light'}`}>
        <div className="bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>

        <div className="particles">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="particle" style={{ bottom: '0' }}></div>
          ))}
        </div>

        <div className="landing-content">
          <div className="content-wrapper">
            <div className="greeting">HELLO THERE</div>
            
            <h1 className="name">
              I'M <span>{Landingdata.name}</span>
            </h1>
            
            <div className="title-container">
              <h2 className="title">
                <span className="typewriter-text">{currentRole}</span>
                <span className="cursor"></span>
              </h2>
            </div>
            
            <p className="description">{Landingdata.info}</p>
            
            <div className="cta-buttons">
              <a href="#contactsection" className="btn btn-primary">
                <span>Let's Connect</span>
                <span aria-hidden="true">-&gt;</span>
              </a>
              
              <a href="#projectsection" className="btn btn-secondary">
                <span>View Projects</span>
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>

          <div className="hero-orbit" aria-label="Areas of expertise">
            <div className="orbit-ring"></div>
            <div className="orbit-ring"></div>
            <div className="orbit-core">
              <div>
                <span>{selectedFocus.label}</span>
                {selectedFocus.title}
                <p>{selectedFocus.text}</p>
              </div>
            </div>
            {focusItems.map((item) => (
              <button
                className={`tech-chip ${item.className} ${selectedFocus.label === item.label ? 'active' : ''}`}
                type="button"
                key={item.label}
                onClick={() => setSelectedFocus(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Landing;
